import { useMutation, useQuery } from "@tanstack/react-query"
import { BadgeCheckIcon, Loader2Icon } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { SignatureServiceMap } from "@/api/api"
import {
  getSeveranceDocument,
  startSeveranceProcess,
} from "@/api/severance-api"
import { EcertGuide } from "@/components/ecert-guide"
import { PageLoader } from "@/components/page-loader"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card"
import Icon from "@/components/ui/icon"
import { Separator } from "@/components/ui/separator"
import { useCurrentToken } from "@/lib/use-current-token"
import { parseError } from "@/lib/utils"

const SeveranceStartPage = () => {
  const { token_id, document_id, isReady } = useCurrentToken()
  const navigate = useNavigate()

  const query = useQuery({
    queryKey: ["getSeveranceDocument", { document_id, token_id }],
    queryFn: () => getSeveranceDocument({ document_id: document_id! }),
    enabled: isReady && !!document_id,
  })

  const startMutation = useMutation({
    mutationKey: ["startSeveranceProcess"],
    mutationFn: startSeveranceProcess,
    onSuccess: () => {
      navigate(`/severance/${token_id}/documents`)
    },
  })

  if (!isReady || query.isLoading) {
    return <PageLoader />
  }

  if (query.isError) {
    throw query.error
  }

  if (!query.isSuccess || !query.data) {
    throw new Error("Unexpected error")
  }

  const currentDocument = query.data

  if (currentDocument.status === "signed") {
    return (
      <div className="mx-auto md:w-10/12 lg:w-8/12 xl:w-6/12">
        <Card className="shadow-lg">
          <CardContent className="flex flex-col items-center justify-center gap-4 pt-6">
            <div>
              <BadgeCheckIcon size={92} className="text-primary" />
            </div>
            <div>Documento ya fue firmado</div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto md:w-10/12 lg:w-8/12 xl:w-6/12">
      <Card className="shadow-lg">
        <CardHeader>
          <h1 className="text-center text-2xl">Firma de Finiquito</h1>

          <CardDescription className="text-center">
            Has sido invitado a firmar el documento de finiquito{" "}
            <strong>{currentDocument.name}</strong>. Este es un proceso{" "}
            <strong>100% en línea</strong>, no necesitas acudir presencialmente
            a una notaría.
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-6">
          <div>
            <div className="mb-2 flex flex-row items-center justify-start gap-2 md:text-lg">
              <Icon name="file-text" />
              <h2 className="font-bold">Revisión de documentos</h2>
            </div>
            <div className="md:pl-8">
              Podrás revisar el documento de finiquito y los anexos adjuntos
              antes de proceder con la firma.
            </div>
          </div>

          <div>
            <div className="mb-2 flex flex-row items-center justify-start gap-2 md:text-lg">
              <Icon name="building-2" />
              <h2 className="font-bold">Información bancaria</h2>
            </div>
            <div className="md:pl-8">
              Deberás proporcionar los datos de tu cuenta bancaria para recibir
              el pago correspondiente.
            </div>
          </div>

          <div>
            <div className="mb-2 flex flex-row items-center justify-start gap-2 md:text-lg">
              <Icon name="signature" />
              <h2 className="font-bold">Firma Electrónica Avanzada</h2>
            </div>
            <div className="md:pl-8">
              <p>
                Serás redirigido a la plataforma de firma electrónica avanzada
                para completar el proceso de forma segura.
              </p>
              {currentDocument.service === SignatureServiceMap.ECERT && (
                <div className="mt-4">
                  <EcertGuide />
                </div>
              )}
            </div>
          </div>
        </CardContent>

        <Separator className="mb-6" />

        <CardFooter className="flex flex-col gap-4 md:flex-row md:justify-end">
          {startMutation.isError && (
            <Alert variant="destructive" className="w-full">
              {parseError(startMutation.error)}
            </Alert>
          )}

          <Button
            onClick={() => startMutation.mutate()}
            disabled={startMutation.isPending}
            className="w-full md:w-auto"
          >
            {startMutation.isPending ? (
              <>
                <Loader2Icon className="mr-2 animate-spin" size={16} />
                <span>Iniciando...</span>
              </>
            ) : (
              <span>Comenzar</span>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}

export { SeveranceStartPage }
